import ks from "../ReUsableTS/kBrowserShared";
import { kHTMLRow, kHTMLTable } from "../ReUsableTS/kHTML";
import LNG from "../ReUsableTS/kLNG";
// Type-only use (Parent); TypeScript elides this import, so there is no module-load cycle.
import { kHTMLMultiSelect } from "../ReUsableTS/kHTMLMultiSelect";

// kMultiSelectOptionType and kMultiSelectOption, moved verbatim from kHTMLMultiSelect.ts (which re-exports them).
export enum kMultiSelectOptionType{
    All,
    Unique,//If this option is selected none other can be selected
    Normal
}
// Natural (alphabetical + numeric) comparators — the workspace default sort order.
// Named so they can be passed to Array.sort without an anonymous callback.
export function kNaturalStringCompare(A: string, B: string): number {
    if (ks.KErrorMode === true) { return 0; }
    try {
        return ks.Nz(A).localeCompare(ks.Nz(B), undefined, { numeric: true, sensitivity: "base" });
    } catch (e) { ks.KGlobalErrorHandler(e, "kHTMLMultiSelect.kNaturalStringCompare"); return 0; }
}
export function kMultiSelectOptionCompare(A: kMultiSelectOption, B: kMultiSelectOption): number {
    if (ks.KErrorMode === true) { return 0; }
    try {
        return kNaturalStringCompare(A.Text, B.Text);
    } catch (e) { ks.KGlobalErrorHandler(e, "kHTMLMultiSelect.kMultiSelectOptionCompare"); return 0; }
}
export class kMultiSelectOption {
    public Text = "";
    public Visible = true;

    public OptionType:kMultiSelectOptionType = kMultiSelectOptionType.Normal;
    public SelectedOptionSpan: HTMLSpanElement | null = null;
    public RemoveSpan: HTMLSpanElement | null = null; //The small icon to remove the selected option

    public AvailableDiv: HTMLDivElement | null = null;
    public OptInput: HTMLInputElement | null = null;
    
    public AvailableIcon: HTMLSpanElement | null = null;
    public SelectedIcon: HTMLSpanElement | null = null;
    public Parent: kHTMLMultiSelect | null = null;

    /**
     * Optional hover text for BOTH the dropdown row and the selected pill. Default "" leaves
     * every existing caller's markup byte-for-byte unchanged.
     *
     * It is applied inside ToAvailableOption/ToSelectedOption rather than being patched on by
     * the consumer after ToDiv(), because the pill span is DESTROYED AND REBUILT on every
     * OK click, pill removal and SelectAll — so a title set once from outside disappears the
     * first time the user commits a selection. (kFileTypePicker shows each type's extension
     * list this way: "DOC, DOCX".)
     */
    public Tooltip = "";


    public ToSelectedOption = (): HTMLSpanElement => {
        if (ks.KErrorMode === true) {
            return document.createElement("span");
        }
        try {
            if(this.SelectedOptionSpan){
                this.SelectedOptionSpan.remove();
                this.SelectedOptionSpan = null;
            }
            this.SelectedOptionSpan = document.createElement("span");
            this.SelectedOptionSpan.className = "optext";
            if (this.Tooltip !== "") { this.SelectedOptionSpan.title = this.Tooltip; }

            if(this.SelectedIcon){
                this.SelectedOptionSpan.appendChild(this.SelectedIcon);
            }
            const OptionLabel:HTMLSpanElement = document.createElement("span");
            OptionLabel.textContent = this.Text;
            this.SelectedOptionSpan.appendChild(OptionLabel);

            this.RemoveSpan = document.createElement("span");
            this.RemoveSpan.className = "optdel";
            this.RemoveSpan.title = LNG.REMOVE;
            /* U+00D7 MULTIPLICATION SIGN, not the letter "x". A lowercase x is an x-height glyph
             * with no ascender, so its ink sits low in the em box and no amount of centring can
             * line it up with the label beside it; "×" is drawn on the maths centre line. */
            this.RemoveSpan.textContent = "×";
            this.RemoveSpan.addEventListener("click", this.RemoveClick);
            this.SelectedOptionSpan.appendChild(this.RemoveSpan);
            return this.SelectedOptionSpan;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.ToSelectedOption");
            return document.createElement("span");
        }
    };
    public RemoveClick = (Event: MouseEvent): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.SelectedOptionSpan && this.Parent) {
                if(this.Text !== LNG.ALL){
                    this.DeSelectChoice();
                    if(this.Parent.CountNormalUnSelected() === this.Parent.Choices.length){
                        if(this.Parent.NoneSelectedOptionText !== ""){
                            this.Parent.SetSelectedChoices([this.Parent.NoneSelectedOptionText]);
                        }
                    }
                    if(this.Parent && this.Parent.ChangeCB){
                        this.Parent.ChangeCB(this.Parent.SelectedChoices());
                    }
                }else{
                    this.Parent.DeSelectAll()
                }
                if(this.Parent && this.Parent.OKClickCB){
                    this.Parent.OKClickCB();
                }
                Event.preventDefault();
                Event.stopPropagation();

            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.RemoveClick");
            return;
        }
    };
    public ToAvailableOption = (): HTMLDivElement  => {
        if (ks.KErrorMode === true) {
            return document.createElement("div");
        }
        try {
            this.AvailableDiv = document.createElement("div");
            this.OptInput = document.createElement("input");
            this.OptInput.type = "checkbox";
            this.OptInput.addEventListener("click", this.ItemChecked);

            // The row is a one-row TABLE, not a label+span: the optional leading icon, the
            // checkbox and the text each need their own fixed-width cell so every option lines
            // up. (The previous label-based markup is gone — it could not hold the icon column.)
            const RowTable:kHTMLTable = new kHTMLTable();
            RowTable.Table.className = "kMultiChoicOptionTable";
            if (this.Tooltip !== "") { RowTable.Table.title = this.Tooltip; }
            RowTable.Table.addEventListener("click", this.LabelClick);
            const SingleRow:kHTMLRow = RowTable.AddEmptyRow();
            if(this.AvailableIcon){
                const IconCell:HTMLTableCellElement =SingleRow.AddCellWithElement(this.AvailableIcon);
                IconCell.style.width = "20px";
                IconCell.style.maxWidth = "20px";
            }
            const InputCell:HTMLTableCellElement = SingleRow.AddCellWithElement(this.OptInput);
            InputCell.style.width = "18px";
            InputCell.style.maxWidth= "18px"

            const LabelCell:HTMLTableCellElement = SingleRow.AddCell(this.Text);
            LabelCell.style.position = "relative";
            LabelCell.style.top = "-2px";
            this.AvailableDiv.appendChild(RowTable.Table);
            return this.AvailableDiv;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.ToAvailableOption");
            return document.createElement("div");
        }
    };
    public LabelClick = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if(this.OptInput){
                this.OptInput.checked = !this.OptInput.checked;
                this.ItemChecked();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.LabelClick");
            return;
        }
    };
    public SelectChoice = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.OptInput && this.Parent && this.Parent.OuterDiv) {
                this.OptInput.checked = true;
                this.Parent.OuterDiv.appendChild(this.ToSelectedOption());
                this.Parent.RefreshPlaceholder();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.SelectChoice");
            return;
        }
    };
    public DeSelectChoice = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.OptInput && this.Parent && this.Parent.OuterDiv) {
                this.OptInput.checked = false;
                if (this.SelectedOptionSpan) {
                    this.SelectedOptionSpan.remove();
                    this.SelectedOptionSpan = null;
                }
                this.Parent.AllSelected = false;
                this.Parent.RefreshPlaceholder();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.DeSelectChoice");
            return;
        }
    };
    /** A normal option toggled. Ticking the LAST unticked one is the same as ticking "All";
        unticking any one must clear the "All" row, since the set is no longer complete. */
    private NormalItemChecked = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (!this.OptInput || !this.Parent) { return; }
            if (this.OptInput.checked) {
                this.Parent.DeSelectUniques();
                //One more selected - does that mean all?
                if (this.Parent.CountNormalUnSelected() === 0) {
                    this.Parent.SelectAll();
                }
                return;
            }
            //One less selected
            this.Parent.AllSelected = false;
            this.DeSelectChoice();
            if (this.Parent.AllOption && this.Parent.AllOption.OptInput) {
                this.Parent.AllOption.OptInput.checked = false;
                if (this.Parent.AllOption.SelectedOptionSpan) {
                    this.Parent.AllOption.SelectedOptionSpan.remove();
                    this.Parent.AllOption.SelectedOptionSpan = null;
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.NormalItemChecked");
        }
    };

    public ItemChecked = (Event?: MouseEvent): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if(this.OptInput && this.Parent){
                switch (this.OptionType){
                    case kMultiSelectOptionType.All:
                        if(this.OptInput.checked){
                            this.Parent.SelectAll();
                        }else{
                            this.Parent.DeSelectAll();
                        }
                        break;
                    case kMultiSelectOptionType.Unique:
                        if(this.OptInput.checked){
                            this.Parent.DeSelectAll();
                            this.Parent.SetSelectedChoices([this.Text]);
                        }
                        break;
                    case kMultiSelectOptionType.Normal:
                        this.NormalItemChecked();
                        break;
                }
            }
            if(Event){
                Event.stopPropagation();
            }
            if(this.Parent && this.Parent.ChangeCB){
                this.Parent.ChangeCB(this.Parent.SelectedChoices());
            }   
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kMultiSelectOption.ItemChecked");
        }
    };

}
